import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const [translations, setTranslations] = useState({});

  useEffect(() => {
    // Load saved language from localStorage
    const savedLanguage = localStorage.getItem('forfeeder-language') || 'en';
    setLanguage(savedLanguage);
    loadTranslations(savedLanguage);
  }, []);

  const loadTranslations = async (lang) => {
    try {
      const translationModule = await import(`../i18n/${lang}.json`);
      setTranslations(translationModule.default || translationModule);
    } catch (error) {
      console.error(`Failed to load translations for ${lang}:`, error);
      // Fallback to English if translation loading fails
      if (lang !== 'en') {
        try {
          const englishModule = await import('../i18n/en.json');
          setTranslations(englishModule.default || englishModule);
        } catch (fallbackError) {
          console.error('Failed to load fallback English translations:', fallbackError);
        }
      }
    }
  };

  const changeLanguage = async (newLanguage) => {
    setLanguage(newLanguage);
    localStorage.setItem('forfeeder-language', newLanguage);
    await loadTranslations(newLanguage);
  };

  const t = (key, defaultValue = key) => {
    const keys = key.split('.');
    let value = translations;
    
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) {
        break;
      }
    }
    
    return value !== undefined ? value : defaultValue;
  };

  return (
    <LanguageContext.Provider value={{
      language,
      changeLanguage,
      t,
      translations
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
